import "@/components/content/HideAndSeek"
import setupAutocompletePorts from "@/components/elm_autocomplete"
import { setupDatepickerPorts } from "@/components/elm_datepicker"
import ElmModal from "@/components/elm_modal"
import { Elm as ElmSearch } from "@/components/elm/Search/Main.elm"
import { logDecodingError } from "@/helpers/airbrake"
import { atou, utoa } from "@/helpers/base64"
import Cookies from "@/helpers/cookies"
import { pushGa4Event, saveGa4ToStorage } from "@/helpers/ga4_helper"
import { startPageLoader } from "@/helpers/page_loader"
import { ENTRY, prefetchEntry } from "@/helpers/prefetch"
import { type SearchFlags, type SearchPorts, addTurboFiltersHandlers } from "@/helpers/search"

declare global {
  interface Window {
    elmSearchFlags: SearchFlags
  }
}

const SEARCH_COOKIE = "search"
const DEFAULT_SEARCH_PATH = "/search"
const PAGE = Object.freeze({
  HOME: "home",
  SEARCH: "search",
  VENUE: "venue",
  ERROR: "error"
} as const)

const redirect = (path: string): void => {
  path ||= DEFAULT_SEARCH_PATH
  if (path === window.location.href.replace(window.location.origin, "")) return

  startPageLoader()
  window.location.assign(path)
}

const removeKey = (key: string, { [key as keyof object]: _, ...rest }: object): object => rest

const removeKeys = (keys: readonly string[], obj: object): object =>
  keys.reduce((acc, key) => removeKey(key, acc), obj)

const sessionFlagsExcept = (keys: string[]): Flags => {
  let session

  try {
    const cookie = atou(Cookies.get(SEARCH_COOKIE) || "")
    session = JSON.parse(decodeURIComponent(cookie))
  } catch {
    session = {}
  }

  return <Flags>removeKeys(keys, session)
}

const sessionFlags = (): Flags => sessionFlagsExcept([])

const setupSearch = (flags: SearchFlags, onAutocompleteFocus?: () => void): ElmApp<SearchPorts> => {
  const modal = new ElmModal({ containerId: "elm-search-container" })
  const app = ElmSearch.Search.Main.init({ node: modal.node(), flags })

  app.ports.flagDecodingError.subscribe(() => {
    logDecodingError(flags)
  })
  app.ports.modalOpened.subscribe(modal.trapFocus)
  app.ports.pushGa4Event.subscribe(pushGa4Event)
  app.ports.ready.subscribe((state: Flags) => {
    Cookies.set(SEARCH_COOKIE, utoa(JSON.stringify(state)))
  })
  app.ports.saveGa4ToStorage.subscribe(saveGa4ToStorage)
  app.ports.search.subscribe(redirect)

  window.addEventListener("resize", app.ports.viewportChanged.send.bind(null, null))

  setupAutocompletePorts({ app, data: flags.autocomplete, onFocus: onAutocompleteFocus })
  setupDatepickerPorts(app)

  return app
}

const search = (page: (typeof PAGE)[keyof typeof PAGE]): void => {
  if (!window.elmSearchFlags) return

  let flags = { ...window.elmSearchFlags, page }

  switch (page) {
    case PAGE.HOME:
    case PAGE.ERROR:
      flags = { ...flags, ...sessionFlags() }
      break
    case PAGE.VENUE:
      flags = { ...flags, ...sessionFlagsExcept(["date", "lengthsOfStay"]) }
      break
  }

  if (page === PAGE.SEARCH) {
    const app = setupSearch(flags)
    addTurboFiltersHandlers(app.ports)
  } else {
    setupSearch(flags, prefetchEntry.bind(null, ENTRY.SEARCH))
  }
}

const initErrorSearch = search.bind(null, PAGE.ERROR)
const initHomeSearch = search.bind(null, PAGE.HOME)
const initSearch = search.bind(null, PAGE.SEARCH)
const initVenueSearch = search.bind(null, PAGE.VENUE)

export { initErrorSearch, initHomeSearch, initSearch, initVenueSearch }
